import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/auth.service';
import { eventService } from '@/services/event.service';

export const AddTaskDialog = ({ open, onOpenChange, onAddTask, weddingId }) => {
  const { user, activeOrg } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'todo', // Default status
    assignedTo: '',
    assignedToName: '',
    eventId: '',
    dueDate: '',
    notes: ''
  });

  // Load team members and events from API
  useEffect(() => {
    const fetchData = async () => {
      if (!activeOrg?.id || !weddingId) return;
      
      try {
        setLoadingTeam(true);
        setLoadingEvents(true);
        const [members, eventsData] = await Promise.all([
          authService.getTeamMembers(activeOrg.id),
          eventService.getEvents(weddingId, activeOrg.id)
        ]);
        setTeamMembers(members);
        setEvents(eventsData);
      } catch (error) {
        console.error('Failed to load data:', error);
        toast.error('Failed to load team members or events');
      } finally {
        setLoadingTeam(false);
        setLoadingEvents(false);
      }
    };
    
    if (open) {
      fetchData();
    }
  }, [open, activeOrg, weddingId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }

    if (!formData.eventId) {
      toast.error('Event is required');
      return;
    }

    setLoading(true);
    
    setTimeout(() => {
      onAddTask(formData);
      toast.success('Task added successfully!');
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        status: 'todo',
        assignedTo: '',
        assignedToName: '',
        eventId: '',
        dueDate: '',
        notes: ''
      });
      setLoading(false);
      onOpenChange(false);
    }, 500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold gradient-text">Add New Task</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="title">Task Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Confirm Mandap Decoration"
                className="mt-1.5"
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add task details..."
                className="mt-1.5"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High Priority</SelectItem>
                    <SelectItem value="medium">Medium Priority</SelectItem>
                    <SelectItem value="low">Low Priority</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="assignedTo">Assigned To</Label>
                <Select 
                  value={formData.assignedTo} 
                  onValueChange={(value) => {
                    const member = teamMembers.find(m => m.id === value);
                    setFormData({ 
                      ...formData, 
                      assignedTo: value,
                      assignedToName: member?.name || ''
                    });
                  }}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.map(member => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name} ({member.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="eventId">Event *</Label>
              <Select 
                value={formData.eventId || undefined} 
                onValueChange={(value) => {
                  const selectedEvent = events.find(e => e.id === value);
                  let dueDate = '';
                  if (selectedEvent?.date) {
                    const d = new Date(selectedEvent.date);
                    dueDate = isNaN(d.getTime())
                      ? String(selectedEvent.date).slice(0, 10)
                      : d.toISOString().split('T')[0];
                  }
                  setFormData({ 
                    ...formData, 
                    eventId: value || undefined,
                    dueDate
                  });
                }}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select an event from timeline" />
                </SelectTrigger>
                <SelectContent>
                  {loadingEvents ? (
                    <SelectItem value="loading" disabled>Loading events...</SelectItem>
                  ) : events.length === 0 ? (
                    <SelectItem value="no-events" disabled>No events available</SelectItem>
                  ) : (
                    <>
                      {events.map(event => (
                        <SelectItem key={event.id} value={event.id}>
                          {event.name} - {new Date(event.date).toLocaleDateString()}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes or reminders..."
                className="mt-1.5"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="gradient-primary" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Task'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTaskDialog;
